// Settings > On this device > Carry code. Only the buttons and the "works until" line live in the
// Settings bundle; the sheet itself (settings/carry-sheet.js) is a lazy chunk loaded on first use.
import { button } from "../ui.js";
import { backupActions } from "./privacy.js";
import { noteSafe } from "../../shared/health-gate.js";
import { reloadApp, downloadJSON } from "../../shared/backup.js";
import { list, drop, saveBlobOf, KEEP_MS } from "../../shared/carry-held.js";

let url = "";
try { url = __RVM.carryJs; } catch { /* unit tests have no build defines */ }
let mod = null;
const load = () => (mod || (mod = import(url).catch((e) => { mod = null; throw e; })));

const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
function saved() {
	try { const c = (JSON.parse(localStorage.getItem("revault:health") || "{}") || {}).carry; return c && c.exp > Date.now() && c.id && c.del ? c : null; } catch { return null; }
}
const day = (t) => new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" });

export function carryRow() {
	const box = el("div", "rv-carry");
	const line = el("p", "rv-account-note");
	const lineDel = button("Delete it", async () => {
		lineDel.disabled = true;
		try { const m = await load(); await m.deleteSaved(); paint(); }
		catch (e) { line.textContent = e && e.kind === "off" ? "Carry codes are turned off right now, so it can't be deleted here. It runs out on its own." : "That didn't work. Try again in a minute."; lineDel.disabled = false; }
	});
	const lineRow = el("div", "rv-account-actions");
	lineRow.append(line, lineDel);
	const make = button("Make a code", () => open("makeFlow"), "rv-btn-primary");
	const use = button("Use a code", () => open("useFlow"));
	const actions = el("div", "rv-account-actions");
	actions.append(make, use);
	const sheet = el("div", "rv-carry-sheet");
	sheet.hidden = true;
	const status = el("p", "rv-account-note");
	status.setAttribute("role", "status");
	box.append(lineRow, actions, sheet, status);

	function paint() {
		const c = saved();
		// The button hides with its line, so nothing (a #set= jump, a search) can land on it alone.
		lineRow.hidden = lineDel.hidden = !c;
		if (c) line.textContent = "Your carry code works until " + day(c.exp) + ".";
		lineDel.disabled = false;
	}
	function done() {
		sheet.hidden = true;
		sheet.textContent = "";
		actions.hidden = false;
		paint();
		make.focus();
	}
	async function open(which) {
		status.textContent = "";
		let m;
		try { m = await load(); }
		catch { status.textContent = "Couldn't load this part of Settings. Check your connection and try again."; return; }
		actions.hidden = true;
		lineRow.hidden = lineDel.hidden = true;
		sheet.hidden = false;
		m[which]({
			host: sheet, done, noteSafe,
			reload: reloadApp,
			saveFile: () => backupActions.backupFile(),
			restoreFile: () => backupActions.restoreFile(),
		});
	}
	paint();
	return box;
}

// Carry codes' last opens that weren't added yet (shared/carry-held.js), offered at the top of
// Settings every time it opens until each is added, kept or thrown away. Every copy gets its own
// box and its own buttons, and each button touches only its own copy.
export function heldOffer() {
	const box = el("div", "rv-carry-held");
	box.hidden = true;
	box.setAttribute("role", "region");
	box.setAttribute("aria-label", "Carry copies not added yet");
	const head = el("p", "rv-account-note");
	box.append(head);
	let left = 0;
	const count = () => {
		head.textContent = left > 1 ? "You have " + left + " carry copies that weren't added yet. Each stays on this device for 7 days." : "";
		head.hidden = left < 2;
	};
	list().then((recs) => {
		if (!recs.length) return;
		left = recs.length;
		for (const rec of recs) box.append(oneHeld(rec, recs.length > 1, () => { left--; count(); }));
		count();
		box.hidden = false;
	}).catch(() => {});
	return box;
}

function oneHeld(rec, many, onGone) {
	const card = el("div", "rv-carry-held-one");
	const text = el("p", "rv-account-note");
	text.textContent = many
		? "Carry copy from " + day(rec.t) + ". It stays on this device until " + day(rec.t + KEEP_MS) + "."
		: "You have a carry copy that wasn't added yet. It stays on this device until " + day(rec.t + KEEP_MS) + ".";
	const status = el("p", "rv-account-note");
	status.setAttribute("role", "status");
	const acts = el("div", "rv-account-actions");
	let live = true, kind = "file";
	const gone = (msg) => { live = false; acts.hidden = true; text.hidden = true; status.textContent = msg; onGone(); };
	const add = button("Add it", async () => {
		if (!live) return;
		add.disabled = true;
		let res = null;
		try { const m = await load(); res = await m.addHeld(rec); }
		catch { status.textContent = "Couldn't load this part of Settings. Check your connection and try again."; add.disabled = false; return; }
		if (!res || !res.ok) { status.textContent = "Some of it didn't fit in this browser. Download it as a save file or copy it as text, then make room and restore it."; add.disabled = false; return; }
		gone(res.text + " Reloading…");
		Promise.resolve(noteSafe("restore")).then(() => setTimeout(() => reloadApp(), 600));
	}, "rv-btn-primary");
	const got = button("I have the file", async () => {
		if (!live) return;
		await drop(rec.id);
		noteSafe(kind);
		gone(kind === "copy" ? "Kept. To bring it back, save the text as a .json file and use Restore from a file." : "Kept. Restore it any time with Restore from a file.");
	});
	got.hidden = true;
	const dl = button("Download", () => {
		if (!live) return;
		downloadJSON("revault-carry-save.json", saveBlobOf(rec));
		status.textContent = "Your save file should be downloading. Once you see it in your downloads, press I have the file.";
		kind = "file";
		got.textContent = "I have the file";
		got.hidden = false;
	});
	const copy = button("Copy as text", async () => {
		if (!live) return;
		try {
			await navigator.clipboard.writeText(JSON.stringify(saveBlobOf(rec)));
			status.textContent = "Copied. Paste it somewhere you keep, like an email to yourself, then press I've kept it.";
			kind = "copy";
			got.textContent = "I've kept it";
			got.hidden = false;
		} catch { status.textContent = "Couldn't copy. Try Download."; }
	});
	const toss = button("Throw it away", async () => {
		if (!live) return;
		if (!confirm("Throw away this carry copy? The code can't be opened again, so it's gone for good.")) return;
		await drop(rec.id);
		gone("Thrown away.");
	}, "rv-btn-danger");
	acts.append(add, dl, copy, toss, got);
	card.append(text, acts, status);
	return card;
}
